<template>
  <div
    class="p-2 sm:p-4 md:p-6 bg-gradient-to-br from-blue-50 to-white w-full min-h-screen"
  >
    <div class="max-w-3xl mx-auto w-full">
      <h2 class="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center">
        My guest5of5 Bookings
      </h2>
      <p class="text-gray-600 mb-4 text-center">
        Manage all your hotel reservations and pre-checkin details in one place.
      </p>

      <div v-if="loading" class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
      <div v-else-if="error" class="text-center py-8 text-red-500">
        <i class="bi bi-exclamation-triangle text-4xl mb-4"></i>
        <p>{{ error }}</p>
      </div>
      <div v-else-if="bookings.length === 0" class="text-center py-8 text-gray-500">
        <i class="bi bi-calendar-x text-4xl mb-4"></i>
        <p>No bookings found.</p>
      </div>
      <div v-else class="space-y-4">
        <div
          v-for="booking in bookings"
          :key="booking.id"
          class="bg-white rounded-lg shadow p-4 flex flex-col gap-2"
        >
          <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <div>
              <h3 class="font-semibold text-lg">{{ booking.hotel?.name || "Hotel" }}</h3>
              <div class="text-xs text-gray-500">Booking ID: {{ booking.id }}</div>
            </div>
            <div class="flex gap-2 text-xs sm:text-sm">
              <span class="bg-blue-100 text-blue-700 px-2 py-1 rounded">{{
                booking.reservation_status
              }}</span>
              <span class="bg-green-100 text-green-700 px-2 py-1 rounded">{{
                booking.payment_status
              }}</span>

                <span class="bg-blue-100 text-blue-700 px-2 py-1 rounded" v-if="booking.precheckin_sent === 2">Pre-checkin
                </span>

                <b-button
                    variant="success"
                    size="sm"
                    @click="goToPreCheckinChat(booking)"
                >
                    <i class="fas fa-comments"></i>
                </b-button>
                <b-button
                    variant="warning"
                    size="sm"
                    @click="showOffers(booking)"
                    v-if="canShowOffers(booking)"
                >
                    <i class="fas fa-gift"></i>
                </b-button>
            </div>
          </div>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs sm:text-sm">
            <div><strong>Check-in:</strong> {{ booking.check_in }}</div>
            <div><strong>Check-out:</strong> {{ booking.check_out }}</div>
            <div><strong>Adults:</strong> {{ booking.num_of_adults }}</div>
            <div><strong>Children:</strong> {{ booking.num_of_child }}</div>
          </div>
          <div class="text-xs sm:text-sm text-gray-700 mt-2">
            <strong>Room Types:</strong>
            <ul class="list-disc ml-5">
              <li v-for="rt in booking.booking_room_types" :key="rt.id">
                {{ rt.room_type?.name }} ({{ rt.num_of_rooms }} room<span
                  v-if="rt.num_of_rooms > 1"
                  >s</span
                >)
                <ul class="list-disc ml-5">
                  <li v-for="room in rt.booking_room" :key="room.id">
                    <span v-if="room.room">Room #{{ room.room.room_number }}</span>
                    <span v-if="room.guests && room.guests.length">
                      - Guests:
                      <span v-for="g in room.guests" :key="g.id">{{ g.name }}</span>
                    </span>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
          <div
            class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mt-2"
          >
            <div class="text-xs sm:text-sm text-gray-500">
              Booked on: {{ booking.created_at }}
            </div>
            <div class="text-xs sm:text-sm font-semibold">
              Total: ₹{{ booking.total_price }}
            </div>
            <button
              v-if="canShowCancel(booking)"
              @click="cancelBooking(booking.id)"
              class="mt-2 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs sm:text-sm"
              :disabled="cancellingId === booking.id"
            >
              {{ cancellingId === booking.id ? "Cancelling..." : "Cancel Booking" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Offers Modal -->
    <b-modal
      v-model="showOffersModal"
      title="Available Offers"
      size="lg"
      :hide-footer="true"
    >
      <div v-if="offersLoading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else-if="availableOffers.length > 0" class="offers-section">
        <div class="offers-grid">
          <div
            v-for="offer in availableOffers"
            :key="offer.id"
            class="offer-card"
            :class="{ 'selected': selectedOffer?.id === offer.id }"
            @click="selectOffer(offer)"
          >
            <div class="offer-header">
              <div class="offer-badge">
                <i class="bi bi-star-fill text-warning"></i>
                {{ offer.display_text }}
              </div>
              <div class="offer-discount">
                -${{ (Number(offer.discount_amount) || 0).toFixed(2) }}
              </div>
            </div>

            <div class="offer-content">
              <h5 class="offer-name">{{ offer.name }}</h5>
              <p v-if="offer.description" class="offer-description">
                <span v-html="offer.description"></span>
              </p>

              <div class="offer-details">
                <div class="price-breakdown">
                  <span class="original-price">${{ (Number(currentBookingTotal) || 0).toFixed(2) }}</span>
                  <span class="final-price">${{ (Number(offer.final_amount) || 0).toFixed(2) }}</span>
                </div>

                <div v-if="offer.conditions" class="offer-conditions">
                  <small class="text-muted">
                    <i class="bi bi-info-circle me-1"></i>
                    {{ getConditionsText(offer.conditions) }}
                  </small>
                </div>
              </div>
            </div>

            <div class="offer-actions">
              <button
                type="button"
                class="btn btn-sm"
                :class="selectedOffer?.id === offer.id ? 'btn-success' : 'btn-outline-primary'"
                @click.stop="selectOffer(offer)"
              >
                <i class="bi" :class="selectedOffer?.id === offer.id ? 'bi-check-circle' : 'bi-plus-circle'"></i>
                {{ selectedOffer?.id === offer.id ? 'Selected' : 'Apply' }}
              </button>
            </div>
          </div>
        </div>

        <div class="mt-3 text-center">
          <b-button
            variant="success"
            @click="applySelectedOffer"
            :disabled="!selectedOffer"
          >
            Apply Offer
          </b-button>
          <b-button variant="secondary" @click="closeOffersModal" class="ml-2">
            Close
          </b-button>
        </div>
      </div>

      <div v-else class="no-offers">
        <div class="text-center py-4 text-muted">
          <i class="bi bi-gift text-muted mb-3" style="font-size: 2rem;"></i>
          <p>No special offers available for this booking</p>
        </div>
        <div class="text-center">
          <b-button variant="secondary" @click="closeOffersModal">
            Close
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { request } from "@/Util/Request";

export default {
  name: "UserBookings",
  data() {
    return {
      bookings: [],
      loading: true,
      error: null,
      cancellingId: null,
      role: null,
      // Offers related data
      showOffersModal: false,
      offersLoading: false,
      availableOffers: [],
      selectedOffer: null,
      currentBooking: null,
      currentBookingTotal: 0,
    };
  },
  async created() {
    this.fetchBookings();
  },
  methods: {
    async fetchBookings() {
      this.loading = true;
      try {
        const response = await request({ method: "get", url: "/my-bookings" });
        this.bookings = response.data || [];
      } catch (error) {
        this.error = "Failed to load bookings. Please try again later.";
        this.bookings = [];
      } finally {
        this.loading = false;
      }
    },
    async cancelBooking(id) {
      if (!confirm("Are you sure you want to cancel this booking?")) return;
      this.cancellingId = id;
      try {
        const response = await request({ method: "put", url: `/cancel-booking/${id}` });
        console.log(response);
        if (response && response.data === true) {
          await this.fetchBookings();
        }
      } catch (error) {
        alert("Failed to cancel booking.");
      } finally {
        this.cancellingId = null;
      }
    },
    canShowCancel(booking) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // strip time for accurate comparison

      const checkInDate = new Date(booking.check_in);
      checkInDate.setHours(0, 0, 0, 0);

      const isFuture = checkInDate > today;
      const isNotCancelled =
        booking.reservation_status !== "cancelled" &&
        booking.reservation_status !== "canceled";

      return isFuture && isNotCancelled;
    },
      goToPreCheckinChat(item) {
          const query = {
              booking_id: item.id,
          };

          if (item.pre_checkin) {
              query.pre_checkin_id = item.pre_checkin.id;
          }

          this.$router.push({
              path: '/pre-check-in/chat',
              query,
          });
      },
      canShowOffers(booking) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const checkInDate = new Date(booking.check_in);
        checkInDate.setHours(0, 0, 0, 0);

        // Show offers for future bookings and today's bookings
        const isTodayOrFuture = checkInDate >= today;
        const isNotCancelled =
          booking.reservation_status !== "cancelled" &&
          booking.reservation_status !== "canceled";

        return isTodayOrFuture && isNotCancelled;
      },
      async showOffers(booking) {
        this.currentBooking = booking;
        this.currentBookingTotal = booking.total_price;
        this.showOffersModal = true;
        this.selectedOffer = null;
        await this.fetchOffers(booking);
      },
      async fetchOffers(booking) {
        this.offersLoading = true;
        try {
          // Prepare room types data for the offers API
          const roomTypes = booking.booking_room_types?.map(brt => ({
            room_type_id: brt.room_type?.id,
            num_of_rooms: brt.num_of_rooms
          })) || [];

          const response = await request({
            method: 'post',
            url: '/public/available-offers',
            data: {
              hotel_id: booking.hotel?.id,
              check_in: booking.check_in,
              check_out: booking.check_out,
              room_types: roomTypes,
              total_amount: booking.total_price
            }
          });

          if (response.success) {
            this.availableOffers = response.data || [];
          } else {
            this.availableOffers = [];
          }
        } catch (error) {
          console.error('Error fetching offers:', error);
          this.availableOffers = [];
        } finally {
          this.offersLoading = false;
        }
      },
      selectOffer(offer) {
        if (this.selectedOffer?.id === offer.id) {
          this.selectedOffer = null;
        } else {
          this.selectedOffer = offer;
        }
      },
      async applySelectedOffer() {
        if (!this.selectedOffer || !this.currentBooking) return;

        try {
          // Build room_types payload from current booking
          const roomTypes = (this.currentBooking.booking_room_types || []).map(brt => ({
            room_type_id: brt.room_type?.id,
            num_of_rooms: brt.num_of_rooms,
          }));

          const payload = {
            offer_id: this.selectedOffer.id,
            hotel_id: this.currentBooking.hotel?.id,
            check_in: this.currentBooking.check_in,
            check_out: this.currentBooking.check_out,
            room_types: roomTypes,
            total_amount: Number(this.currentBooking.total_price || 0),
          };

          const response = await request({
            method: 'post',
            url: '/public/apply-offer',
            data: payload,
          });

          if (response.success) {
            const discount = response.data?.discount_amount;
            const finalAmount = response.data?.final_amount;
            this.$bvToast.toast(
              finalAmount != null
                ? `Offer applied! Discount: ₹${Number(discount || 0).toFixed(2)} | Final: ₹${Number(finalAmount).toFixed(2)}`
                : 'Offer applied successfully!',
              {
                title: 'Success',
                variant: 'success',
                solid: true,
              }
            );

            // Optionally refresh bookings list
            await this.fetchBookings();
            this.closeOffersModal();
          } else {
            this.$bvToast.toast(response.message || 'Failed to apply offer', {
              title: 'Error',
              variant: 'danger',
              solid: true,
            });
          }
        } catch (error) {
          console.error('Error applying offer:', error);
          const msg = error?.data?.message || 'Failed to apply offer. Please try again.';
          this.$bvToast.toast(msg, {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        }
      },
      closeOffersModal() {
        this.showOffersModal = false;
        this.selectedOffer = null;
        this.currentBooking = null;
        this.availableOffers = [];
      },
      getConditionsText(conditions) {
        if (!conditions) return '';

        const texts = [];
        if (conditions.min_nights) {
          texts.push(`Minimum ${conditions.min_nights} nights`);
        }
        if (conditions.max_nights) {
          texts.push(`Maximum ${conditions.max_nights} nights`);
        }
        if (conditions.min_order_amount) {
          texts.push(`Minimum order $${conditions.min_order_amount}`);
        }

        return texts.join(', ');
      }
  },
};
</script>

<style scoped>
.offers-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

.offers-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
}

.offer-card {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 1.25rem;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.offer-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border-color: #007bff;
}

.offer-card.selected {
  border-color: #28a745;
  background: linear-gradient(135deg, #f8fff9 0%, #ffffff 100%);
  box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.2), 0 4px 15px rgba(0, 0, 0, 0.1);
}

.offer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.offer-badge {
  background: linear-gradient(135deg, #ffc107 0%, #ff8c00 100%);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.offer-discount {
  background: #28a745;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
}

.offer-content {
  margin-bottom: 1rem;
}

.offer-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.offer-description {
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.offer-details {
  margin-bottom: 1rem;
}

.price-breakdown {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.original-price {
  color: #6c757d;
  text-decoration: line-through;
  font-size: 0.9rem;
}

.final-price {
  color: #28a745;
  font-weight: 600;
  font-size: 1.1rem;
}

.offer-conditions {
  font-size: 0.8rem;
  color: #6c757d;
}

.offer-actions {
  display: flex;
  justify-content: flex-end;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 6px;
}

.no-offers {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

@media (max-width: 768px) {
  .offers-grid {
    grid-template-columns: 1fr;
  }

  .offer-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>
